import { getCookie, setCookie, updateCookie, text_truncate, getBrowserType, installInpObserver, getTrackingNonce, waitForFreshNonce, installTapMoveGuard, shouldIgnoreActivation, normalizeCodePlaceholder } from '../core/utils.js';

var code = '',
    ccode = '',
    leads_referrer = document.referrer;

/*
  trial-buttons.js

  Ringkasan:
  Script ini meng-intercept (mencegat) klik link Trial ke halaman sign up (my.jurnal.id/.../users/sign_up)
  supaya:
  1) Loader/overlay tampil instan => user tidak klik berkali-kali.
  2) Navigasi lebih stabil: buka di tab baru dengan teknik yang lebih tahan popup-blocker.
  3) Tracking lebih reliabel: simpan ke DB via admin-ajax terlebih dulu, lalu fallback ke Google Sheets
     jika DB gagal (mis. error server / policy / jaringan).

  Kenapa perlu capture phase + dedupe?
  - Handler diikat ke 'click' + 'pointerup' di window + document, jadi 1 tap bisa
    memicu handler 2-3x (dedupe mencegahnya). pointerup dipakai supaya tap pertama
    setelah scroll di mobile tetap jalan; shouldIgnoreActivation() menyaring klik
    kanan & scroll/drag agar tidak jadi phantom click.
  - Ada script lain di theme yang mungkin juga mengikat handler klik.
    Dengan capture:true + stopPropagation, kita pastikan alur navigasi tidak "dibajak".

  Catatan penting:
  - Script ini hanya memproses link sign_up tertentu.
  - Untuk elemen lain, script tidak melakukan apa-apa.
*/

export function trialButton() {

  // Prevent double-binding if this function is called multiple times.
  if (window.__jurnalTrialButtonBound) return;
  window.__jurnalTrialButtonBound = true;

  installInpObserver();
  installTapMoveGuard();

  /*
    Konfigurasi endpoint WordPress:
    - Kita ambil base dari <link rel="https://api.w.org/"> jika ada.
    - Lalu turunkan ke URL admin-ajax (wp-admin/admin-ajax.php).
    Alasan: beberapa environment memblokir wp-json (REST) dengan 403, sedangkan admin-ajax biasanya aman.
    Tapi diprioritaskan dulu window.mlatConfig.ajaxUrl (dari admin_url() di PHP) — di
    situs multilingual, <link rel="https://api.w.org/"> bisa balik ber-prefix locale
    (mis. /en/wp-json/), padahal admin-ajax.php nggak pernah ada di bawah prefix itu.
  */

  const wpApiLink = document.querySelector('link[rel="https://api.w.org/"]');
  const wpJsonBase = (wpApiLink && wpApiLink.href)
    ? wpApiLink.href.replace(/\/?$/, '/')
    : (window.location.origin + '/wp-json/');
  const wpSiteBase = wpJsonBase.replace(/wp-json\/?$/, '');
  const wpAjaxURL = (window.mlatConfig && window.mlatConfig.ajaxUrl)
    || (wpSiteBase.replace(/\/?$/, '/') + 'wp-admin/admin-ajax.php');

  const setDisplayImportant = (el, value) => {
    // Paksa display dengan !important supaya overlay benar-benar terlihat meski ada CSS yang override.
    try {
      el.style.setProperty('display', value, 'important');
    } catch (e) {
      el.style.display = value;
    }
  };

  const showLoading = () => {
    // Prioritas 1: gunakan API global jika tersedia (loader-guard.js)
    // supaya semua script punya tampilan loader yang konsisten.
    if (typeof window.__jurnalShowLoading === 'function') {
      window.__jurnalShowLoading();
      return;
    }
    // Fallback: nyalakan semua elemen .has-loading yang ada di halaman.
    document.querySelectorAll('.has-loading').forEach(el => setDisplayImportant(el, 'flex'));
  };

  const hideLoading = () => {
    if (typeof window.__jurnalHideLoading === 'function') {
      window.__jurnalHideLoading();
      return;
    }
    document.querySelectorAll('.has-loading').forEach(el => setDisplayImportant(el, 'none'));
  };

  const scheduleIdle = (fn) => {
    // Menunda pekerjaan non-kritis (tracking / olah cookie) agar tidak mengganggu respons klik.
    // requestIdleCallback bila ada -> lebih ramah performa (INP). fallback setTimeout.
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(fn, { timeout: 1500 });
    } else {
      setTimeout(fn, 0);
    }
  };

  const browserType = getBrowserType();

  const isMobileUA = (() => {
    // Deteksi sederhana (dan cukup cepat) untuk menentukan device type.
    // Tidak pakai regex super panjang agar tidak berat di path klik.
    const ua = (navigator.userAgent || '').toLowerCase();
    if (ua.includes('ipad')) return true;
    if (ua.includes('iphone')) return true;
    if (ua.includes('ipod')) return true;
    if (ua.includes('android')) return true;
    return ua.includes('mobile');
  })();
  const deviceType = isMobileUA ? 'Mobile' : 'Desktop';

  const makeClickShort = () => {
    // Membuat ID pendek berbasis waktu.
    // Dipakai sebagai "short"/request id (mis. [Req.JDDMMYYhhmmssms]) untuk:
    // - menandai event tracking
    // - memudahkan audit/debug
    const d = new Date();
    const DD = String(d.getDate()).padStart(2, '0');
    const MM = String(d.getMonth() + 1).padStart(2, '0');
    const YY = String(d.getFullYear()).slice(2);
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds());
    const ms = String(d.getMilliseconds());
    return DD + MM + YY + hh + mm + ss + ms;
  };

  // Appends UTM/GCLID as query params to the sign-up URL — needed when the
  // destination page lives on a domain/subdomain that can't read this site's
  // `campaign_info` cookie. Opt-in per site via wp-admin → Trial Tracking →
  // "Append UTM & GCLID to Trial URL" (default off — sites whose sign-up page
  // shares the cookie domain don't need this).
  const appendTrialUrlParams = (url, params) => {
    try {
      const u = new URL(url, window.location.href);
      Object.keys(params).forEach((k) => {
        if (params[k]) u.searchParams.set(k, params[k]);
      });
      return u.toString();
    } catch (e) {
      return url;
    }
  };

  const postTrialToDbAjax = async (payload) => {
    // DB-first: kirim payload ke endpoint admin-ajax.
    // keepalive:true supaya request tetap dikirim walau tab segera pindah/navigasi.
    // Tap cepat setelah landing bisa mendahului fetch nonce baru → nonce HTML yang
    // (dari edge cache) mungkin sudah expired → 403. Tunggu sebentar (maks 1.5s).
    await waitForFreshNonce(1500);
    payload.nonce = getTrackingNonce();
    if (window.__jurnalWabaDebug) console.log('[mlat trial] POST →', wpAjaxURL + '?action=mlat_trial_store');
    const res = await fetch(wpAjaxURL + '?action=mlat_trial_store', {
      method: 'POST',
      // text/plain (bukan application/json): CORS-safelisted, jadi tidak pernah
      // memicu preflight — PHP membaca php://input mentah, jadi JSON tetap terbaca.
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
      keepalive: true
    });

    // Body dibaca juga untuk non-2xx: 403/500 dari PHP membawa `gsheets_sent` —
    // kalau server sudah kirim ke GSheets, JS tidak boleh kirim lagi (duplikat `short`).
    const json = await res.text().catch(() => '').then(text => {
      try { return JSON.parse(text); } catch {
        const m = text.match(/(\{[\s\S]*\})/);
        if (m) try { return JSON.parse(m[1]); } catch {}
        if (window.__jurnalWabaDebug) console.warn('[mlat trial] non-JSON response body:', text.substring(0, 300));
        return null;
      }
    });
    if (window.__jurnalWabaDebug) console.log('[mlat trial] response (HTTP ' + res.status + '):', json);
    if (json === null && res.status === 200) {
      if (window.__jurnalWabaDebug) console.warn('[mlat trial] HTTP 200 but unparseable body — assuming DB OK, skipping GSheets');
      return { success: true };
    }
    if (!json || json.success !== true) {
      const msg = json && json.data && json.data.message ? json.data.message : ('admin-ajax HTTP ' + res.status);
      const err = new Error(msg);
      // Hanya percaya flag eksplisit dari PHP. Body non-JSON + non-200 (mis. 502/504
      // dari CDN) belum tentu sudah dikirim server → biarkan JS fallback jalan.
      err.gsheets_sent = !!(json && json.data && json.data.gsheets_sent);
      throw err;
    }

    return json;
  };

  const buildTrialGSheetsFormData = (payload) => {
    // FormData dipilih karena kompatibel luas dan mudah untuk endpoint GSheets.
    const fd = new FormData();
    fd.append('Short', payload.short || '');
    fd.append('GCLID', payload.gclid || '');
    fd.append('Journey', payload.journey || '');
    fd.append('UTM Term', payload.utm_term || '');
    fd.append('Matchtype', payload.matchtype || '');
    fd.append('Device', payload.device || '');
    fd.append('Button Label & Button ID', payload.button_label_id || '');
    fd.append('Button Journey', payload.button_label_id || '');
    fd.append('GAID', payload.gaid || '');
    fd.append('utm_source', payload.utm_source || '');
    fd.append('utm_medium', payload.utm_medium || '');
    fd.append('utm_campaign', payload.utm_campaign || '');
    fd.append('Banner ID', payload.banner_id || '');
    fd.append('Waba Medium', payload.waba_medium || '');
    fd.append('Browser', payload.browser || '');
    fd.append('Domain', window.location.host || '');
    fd.append('GCLID Journey', payload.gclid_journey || '');
    fd.append('Referrer', payload.referrer || '');
    fd.append('Query Search Term', payload.query_search_term || '');
    fd.append('Visitor ID', payload.visitor_id || '');
    return fd;
  };

  const postTrialToGSheets = (payload, gsheetsURL) => {
    // Fallback: POST FormData ke Google Apps Script.
    return fetch(gsheetsURL, { method: 'POST', body: buildTrialGSheetsFormData(payload), keepalive: true }).catch(() => {});
  };

  const scheduleTrialSendToDbThenFallback = (payload, gsheetsURL) => {
    // Jalankan tracking di waktu idle agar klik tetap responsif.
    // Urutan: DB -> (kalau gagal) GSheets.
    scheduleIdle(() => {
      postTrialToDbAjax(payload)
        .catch((errAjax) => {
          if (errAjax && errAjax.gsheets_sent) {
            if (window.__jurnalWabaDebug) console.log('[mlat trial] GSheets already sent by server, skipping JS fallback');
            return;
          }
          console.error('Trial DB post failed, fallback to GSheets:', errAjax && errAjax.message ? errAjax.message : errAjax);
          postTrialToGSheets(payload, gsheetsURL);
        });
    });
  };

  const getLinkFromEvent = (e) => {
    // Ambil elemen <a> terdekat dari target event.
    // target event bisa Text node => dinormalisasi.
    const rawTarget = e && e.target;
    const target = (rawTarget && rawTarget.nodeType === 3) ? rawTarget.parentElement : rawTarget;
    if (!target || typeof target.closest !== 'function') return null;
    return target.closest('a');
  };

  let _lastHandledTs = 0;
  let _lastHandledEl = null;

  const clickHandler = (e) => {
    // Handler utama: intercept klik Trial.
    // - Hanya memproses jika link mengarah ke sign_up.
    // - Dedupe untuk menghindari 1 tap menghasilkan 2-3 event.
    if (!e) return;
    if (e.__jurnalTrialHandled) return;

    const link = getLinkFromEvent(e);
    if (!link) return;

    const href = link.href || '';
    const _mlatSignupUrls = (window.mlatConfig && window.mlatConfig.trial && window.mlatConfig.trial.signupUrls) || ['my.jurnal.id/id/users/sign_up', 'my.jurnal.id/en/users/sign_up'];
    if (!_mlatSignupUrls.some(u => href.includes(u))) return;

    // Abaikan klik kanan/tengah, pointer sekunder, dan ekor scroll/drag.
    if (shouldIgnoreActivation(e)) return;

    if (window.__jurnalWabaDebug) {
      console.log('[mlat trial] button detected:', {
        id: link.id || '(none)',
        classes: link.className,
        href: href.substring(0, 80),
        isTrusted: e && e.isTrusted,
      });
    }

    // Blok event sintetis/programmatic (mis. `el.click()` / `dispatchEvent`).
    try {
      if (typeof e.isTrusted === 'boolean' && e.isTrusted === false) {
        // Allow synthetic clicks when test/automation bypass flag is set.
        // Usage from Selenium/BrowserStack:
        //   driver.execute_script("window.__jurnalWabaAllowUntrusted = true;")
        if (window.__jurnalWabaAllowUntrusted === true) {
          // fall through — treat as trusted
        } else {
          try { document.documentElement.dataset.jurnalTrialBlockedSynthetic = String(Date.now()); } catch (err) {}
          try { e.__jurnalTrialHandled = true; } catch (err) {}
          try { e.preventDefault(); } catch (err) {}
          try { if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation(); } catch (err) {}
          try { if (typeof e.stopPropagation === 'function') e.stopPropagation(); } catch (err) {}
          try { showLoading(); } catch (err) {}
          return;
        }
      }
    } catch (err) {}

    const now = (window.performance && typeof window.performance.now === 'function')
      ? window.performance.now()
      : Date.now();

    if (_lastHandledEl === link && (now - _lastHandledTs) < 900) {
      try { e.__jurnalTrialHandled = true; } catch (err) {}
      try { e.preventDefault(); } catch (err) {}
      try { if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation(); } catch (err) {}
      try { if (typeof e.stopPropagation === 'function') e.stopPropagation(); } catch (err) {}
      try { showLoading(); } catch (err) {}
      return;
    }
    _lastHandledEl = link;
    _lastHandledTs = now;

    e.__jurnalTrialHandled = true;
    try { e.preventDefault(); } catch (err) {}
    try { if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation(); } catch (err) {}
    try { if (typeof e.stopPropagation === 'function') e.stopPropagation(); } catch (err) {}

    // Write campaign_info cookie synchronously BEFORE opening the tab,
    // so the destination page reads the correct value on load.
    const url = new URL(window.location.href);
    const sp = url.searchParams;
    const cookieCache = new Map();
    const gc = (key) => {
      if (cookieCache.has(key)) return cookieCache.get(key);
      const val = getCookie(key) || '';
      cookieCache.set(key, val);
      return val;
    };
    const click_sh = makeClickShort();
    const gclid = sp.get('gclid') || gc('gclid');
    const usource = sp.get('utm_source') || gc('utm_source');
    const umedium = sp.get('utm_medium') || gc('utm_medium');
    const modifiedCampaign = normalizeCodePlaceholder((window.mlatConfig && window.mlatConfig.trial && window.mlatConfig.trial.ticketFormat) || '[Req.J{{code}}]').replace('{{code}}', click_sh);
    const campaignInfo = {
      gclid: gclid || '',
      utm_source: usource || '',
      utm_medium: umedium || '',
      utm_campaign: modifiedCampaign
    };
    const cookiesCampaignInfo = encodeURIComponent(JSON.stringify(campaignInfo));
    if (gc('created_at')) {
      updateCookie('campaign_info', cookiesCampaignInfo);
      updateCookie('j_campaign_info', cookiesCampaignInfo);
    } else {
      setCookie('campaign_info', cookiesCampaignInfo, 31);
      setCookie('j_campaign_info', cookiesCampaignInfo, 31);
    }

    // 1. Show loader as soon as possible (sync)
    showLoading();

    // 2. Build payload BEFORE navigation — required so tracking fetch is started
    //    before any page unload (mobile same-tab navigation).
    const msclkid = sp.get('msclkid') || gc('msclkid');
    const ucampaign = sp.get('utm_campaign') || gc('utm_campaign');
    const utm_term = sp.get('utm_term') || gc('utm_term');
    const matchtype = sp.get('matchtype') || gc('matchtype');
    const banner_journey = gc('banner_journey');
    const cta_journey = gc('cta_journey');
    const cta_last_click = gc('cta_last_click');
    const medium_code = gc('waba_code');
    const query_keyword = sp.get('query') || gc('query_keyword') || 'null';

    let label = '';
    if (link.classList.contains('ic-wa')) {
      label = 'ID: ' + (link.id || '') + ', Label: Sticky WABA';
    } else if (link.classList.contains('top-banner-sticky') || link.classList.contains('top-banner-bot')) {
      label = 'ID: ' + (link.id || '') + ', Label: Sticky Banner';
    } else if (link.id) {
      label = 'ID: ' + link.id + ', Label: ' + (link.innerText || '');
    } else {
      label = 'ID: , Label: ' + (link.innerText || '');
    }

    let cta_journey_temp = '';
    if (cta_journey) {
      cta_journey_temp = cta_last_click
        ? ((cta_last_click !== (link.id || '')) ? (cta_journey + '|' + label) : cta_journey)
        : label;
    } else {
      cta_journey_temp = label;
    }

    if (cta_journey) { updateCookie('cta_journey', cta_journey_temp); } else { setCookie('cta_journey', cta_journey_temp, 31); }
    if (cta_last_click) { updateCookie('cta_last_click', link.id || ''); } else { setCookie('cta_last_click', link.id || '', 31); }

    const payload = {
      nonce: getTrackingNonce(),
      short: click_sh,
      gclid: text_truncate(`${gclid}|${msclkid}`, 250, ''),
      journey: (gc('user_journey') ? text_truncate(gc('user_journey'), 250, '') : ''),
      utm_term: utm_term,
      matchtype: matchtype,
      device: deviceType,
      button_label_id: cta_journey_temp,
      gaid: (gc('gaid') ? gc('gaid') : (gc('_ga') ? gc('_ga').slice(6) : '')),
      utm_source: usource,
      utm_medium: umedium,
      utm_campaign: ucampaign,
      utm_content: (sp.get('utm_content') || gc('utm_content') || ''),
      campaign_id: (sp.get('campaign_id') || gc('campaign_id') || ''),
      banner_id: banner_journey || '',
      waba_medium: medium_code || '',
      browser: browserType,
      domain: window.location.host || '',
      gclid_journey: (gc('gclid_journey') || ''),
      referrer: (gc('lead_referrer') ? gc('lead_referrer') : (leads_referrer || '')),
      query_search_term: query_keyword,
      visitor_id: (gc('visitor_id') || '')
    };
    const scriptURL = (window.mlatConfig && window.mlatConfig.trial && window.mlatConfig.trial.gsheetsUrl) || 'https://script.google.com/macros/s/AKfycbxEjYVMgbE9GCMJnnpLgwgkcph8xbe1d2a4CQpz2UzF9ijordtPNLpLdOEzHZ3Ur5E/exec';

    if (window.__jurnalWabaDebug) {
      console.log('[mlat trial] payload to DB:', {
        short: payload.short,
        nonce: payload.nonce ? payload.nonce.substring(0, 6) + '…' : '⚠️ MISSING',
        utm_source: payload.utm_source,
        utm_medium: payload.utm_medium,
        utm_campaign: payload.utm_campaign,
        ajaxUrl: wpAjaxURL + '?action=mlat_trial_store',
      });
    }

    // 3. Open new tab (desktop) BEFORE firing tracking, so popup blocker allows it.
    const newTab = isMobileUA ? null : window.open('about:blank', '_blank');
    const _appendUrlParams = (window.mlatConfig && window.mlatConfig.trial && window.mlatConfig.trial.appendUrlParams) === true;
    const $url = _appendUrlParams
      ? appendTrialUrlParams(href, {
          utm_source: usource,
          utm_medium: umedium,
          utm_term: utm_term,
          utm_campaign: modifiedCampaign,
          gclid: gclid,
        })
      : href;

    // 4. Fire tracking + navigate.
    if (newTab) {
      // Desktop — current page stays alive: fire-and-forget is safe.
      postTrialToDbAjax(payload).catch((errAjax) => {
        if (errAjax && errAjax.gsheets_sent) {
          if (window.__jurnalWabaDebug) console.log('[mlat trial] GSheets already sent by server, skipping JS fallback');
          return;
        }
        console.error('[mlat trial] DB failed, fallback to GSheets:', errAjax && errAjax.message ? errAjax.message : errAjax);
        postTrialToGSheets(payload, scriptURL);
      });
      try { newTab.location.href = $url; } catch (e1) {}
      if (browserType === 'Apple Safari (iOS)') {
        setTimeout(() => { try { newTab.location.href = $url; } catch (e2) {} }, 200);
      }
      try { newTab.focus(); } catch (e3) {}
    } else {
      // Mobile — same-tab navigation.
      //
      // Dulu: sendBeacon ke DB + GSheets lalu langsung navigate. Masalahnya
      // sendBeacon() yang return `true` cuma berarti "masuk antrean", bukan
      // "terkirim" — di in-app browser (Instagram/Facebook/TikTok/LINE, WKWebView)
      // dan sebagian iOS Safari, request yang masih antre ikut dibatalkan saat
      // halaman langsung pindah, jadi DB & GSheets sama-sama kosong.
      //
      // Sekarang: DB via fetch keepalive yang DITUNGGU sampai server menjawab.
      // GSheets HANYA dikirim kalau DB gagal DAN server belum kirim ke GSheets
      // sendiri (gsheets_sent) — dan kiriman itu juga DITUNGGU. Tidak ada beacon
      // GSheets paralel lagi: satu klik = satu baris, tidak ada duplikat `short`.
      // Total tunggu maksimal MOBILE_TRACK_MAX_WAIT_MS (loader tampil selama itu);
      // kalau habis, redirect tetap jalan dan fetch keepalive lanjut di belakang —
      // kalau insert-nya gagal di server, mlat_gsheets_fallback() di PHP yang
      // mengirim ke GSheets.
      const MOBILE_TRACK_MAX_WAIT_MS = 3000;

      const navigateMobile = () => {
        window.location.href = $url;
        // Deep-links (wa.me / app scheme) open an external app without
        // unloading the page — the browser tab stays visible. Hide the
        // loader after a grace period so the user isn't stuck on a spinner.
        setTimeout(() => { try { hideLoading(); } catch (_) {} }, 2500);
      };

      const trackPromise = postTrialToDbAjax(payload)
        .then(() => {
          if (window.__jurnalWabaDebug) console.log('[mlat trial] mobile DB OK → navigating');
        })
        .catch((err) => {
          console.error('[mlat trial] DB failed on mobile:', err && err.message ? err.message : err);
          if (err && err.gsheets_sent) {
            if (window.__jurnalWabaDebug) console.log('[mlat trial] GSheets already sent by server, skipping JS fallback');
            return;
          }
          if (window.__jurnalWabaDebug) console.log('[mlat trial] mobile fallback: awaiting GSheets fetch');
          return postTrialToGSheets(payload, scriptURL);
        });

      let navigated = false;
      const navigateOnce = () => {
        if (navigated) return;
        navigated = true;
        navigateMobile();
      };
      trackPromise.then(navigateOnce, navigateOnce);
      setTimeout(() => {
        if (!navigated && window.__jurnalWabaDebug) console.warn('[mlat trial] mobile tracking still pending after ' + MOBILE_TRACK_MAX_WAIT_MS + 'ms → navigating anyway (keepalive fetch continues)');
        navigateOnce();
      }, MOBILE_TRACK_MAX_WAIT_MS);
    }

    setTimeout(hideLoading, 4000);
    return;
  };

  const bind = (target, type) => {
    try {
      // passive:false karena kita memanggil preventDefault.
      // capture:true agar handler ini berjalan lebih dulu.
      target.addEventListener(type, clickHandler, { passive: false, capture: true });
    } catch (e) {}
  };

  // 'click' + 'pointerup': pointerup menangkap tap pertama setelah scroll di mobile
  // (click-nya ditelan browser). shouldIgnoreActivation() menyaring klik kanan & drag.
  ['click', 'pointerup'].forEach((type) => {
    bind(window, type);
    bind(document, type);
  });

  // Lifts the pointer-events guard injected by mlat_inject_trial_guard() (frontend.php):
  // trial links stay untappable until the handler above is bound.
  try { document.documentElement.classList.add('mlat-trial-ready'); } catch (e) {}
}